"use client";

/* eslint-disable react-hooks/exhaustive-deps */
import { Definitions } from "@asosteam/asos-web-datalayer";
import { useEffect } from "react";
import { Facet, Product } from "@repo/apis/browse-search-v2";
import { RecommendationProduct } from "@repo/apis/recommendations";
import { Filters } from "@repo/ui/product-filtering";
import { useAnalytics } from "@/components/analytics/initialise-analytics";
import { mapProducts, mapRecommendations } from "./analytics-helpers";

export type ViewItemListProductAnalyticsProps = {
  recommendations?: RecommendationProduct[];
  products?: Product[];
  item_list_name: Exclude<
    Definitions["view_item_list"],
    undefined
  >["item_list_name"];
  search_term?: string;
  searchType?: string;
  categoryID?: number;
  pagination?: {
    totalProducts: number;
    currentPage: number;
    columns: number;
    itemsPerPage: number;
  };
  facets?: Facet[];
  filters?: Filters;
  item_list_state?: Exclude<
    Definitions["view_item_list"],
    undefined
  >["item_list_state"];
};

const buildRefinementsObject = (facets: Facet[], filters: Filters) => ({
  context: "refinements:applied",
  listAppliedRefinements: facets
    .filter((facet) => facet.hasSelectedValues)
    .filter((facet) => facet.id !== "sort")
    .map((facet) => ({
      refinementId: facet.id,
      refinementName: facet.name,
      selections: facet.facetValues
        .filter((selection) => selection.isSelected)
        .map((selection) => ({
          selectionId: selection.id,
          selectionName: selection.name,
        })),
    })),
  sort: filters.sort
    ? {
        sortName: Array.isArray(filters.sort) ? filters.sort[0] : filters.sort,
      }
    : undefined,
});

export const ViewItemListProductAnalytics = ({
  recommendations,
  products,
  item_list_name,
  search_term,
  searchType,
  categoryID,
  pagination,
  filters,
  facets,
  item_list_state,
}: ViewItemListProductAnalyticsProps) => {
  const { pushToDataLayer } = useAnalytics();

  useEffect(() => {
    let items, localCurrencyCode;
    if (Array.isArray(recommendations) && recommendations.length > 0) {
      items = mapRecommendations(recommendations);
      localCurrencyCode = recommendations[0]?.price.currency;
    } else if (Array.isArray(products) && products.length) {
      items = mapProducts(products);
      localCurrencyCode = products[0]?.price.currency;
    }
    pushToDataLayer({
      view_item_list: {
        item_list_name,
        item_list_state,
        search_term,
        searchType,
        categoryID,
        pageGridSize: pagination?.columns,
        pageNumber: pagination?.currentPage,
        pageSize: pagination?.itemsPerPage,
        totalStyles: pagination?.totalProducts,
        ecommerce: {
          currency: "GBP",
          localCurrencyCode,
          items: items || [],
        },
        refinements:
          facets && filters && buildRefinementsObject(facets, filters),
      },
    });
  }, [recommendations, item_list_name, item_list_state, products]);

  return null;
};
